import { useMemo } from 'react'

import { CreatorPermission } from '@api'
import type { DateRange } from 'react-day-picker'
import { useQuery } from 'react-query'

import type { Creator } from '@/modules/creators/types'
import { trackingService } from '@/modules/tracking/chatter-tracking/service/chatter-tracking-service.ts'
import { handleError } from '@/utils/error'

export const useChatterLeaderboardInspectData = (
  creators: Creator[],
  dateRange: DateRange | undefined,
  userId: number | undefined,
  browserName: string | undefined,
  hourly: boolean,
) => {
  const filteredCreators = creators.filter((creator) =>
    creator.permissions.includes(CreatorPermission.SEE_CHATTER_TRACKING),
  )

  const { data, isFetching, isIdle, refetch } = useQuery(
    [
      'chatter-tracking',
      dateRange,
      userId,
      browserName,
      hourly,
      filteredCreators.map((creator) => creator.id),
    ],
    async () => {
      return await trackingService.findLeaderboardInspectByNameAndDate(
        creators.filter((creator) =>
          creator.permissions.includes(CreatorPermission.SEE_CHATTER_TRACKING),
        ),
        browserName,
        userId,
        dateRange?.from || new Date(),
        dateRange?.to || new Date(),
        hourly,
      )
    },
    {
      retry: false,
      enabled:
        (!!userId || !!browserName) &&
        !!dateRange &&
        filteredCreators.length > 0,
      onError: (error) =>
        handleError(error, 'Failed to get chatter leaderboard data'),
    },
  )

  return useMemo(
    () => ({
      data,
      isFetching: isFetching || isIdle,
      refetch,
    }),
    [data, isFetching, isIdle, refetch],
  )
}
